import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { MailIcon, MapPinIcon, ShieldCheckIcon, LifeBuoyIcon, BugIcon } from 'lucide-react';
import { COMPANY, SUPPORT_EMAIL } from '../data/launch';

/**
 * INTERNAL NOTE — not rendered.
 * The previous contact form showed a success state without sending anything.
 * It has been removed rather than faking delivery. If a real backend or email
 * service is wired up later (for example the same Apps Script endpoint used by
 * the waitlist), a form can be reinstated here — but only with genuine success
 * and failure states driven by the response.
 */

const contactRoutes = [{
  icon: LifeBuoyIcon,
  title: 'Help using KittyPot',
  body: 'Stuck on a kitty, a split or settling up? Tell us what you were trying to do and we will walk you through it.',
  subject: 'Help using KittyPot'
}, {
  icon: BugIcon,
  title: 'Report a problem',
  body: 'Something not working? Include your device, the app version and what happened just before, and we will investigate.',
  subject: 'Problem report'
}, {
  icon: ShieldCheckIcon,
  title: 'Privacy and your data',
  body: 'To access, correct or delete your data, email us from the address on your KittyPot account so we can verify it is you.',
  subject: 'Privacy request'
}];
export function Contact() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-[#FAFAFA] font-sans selection:bg-kitty-yellow-200 selection:text-kitty-dark" data-id="element-411" data-mp="vgpuos8ptx20">
      <SEO title="Contact | KittyPot" description="Get in touch with the KittyPot team for help using the app, to report a problem, or to make a privacy request." ogTitle="Contact KittyPot" ogDescription="Help, problem reports and privacy requests." path="/contact" data-id="element-412" data-mp="10hdlbct18bvp" />
      <Navbar data-id="element-413" data-mp="1m627jcxb85o9" />
      <main className="pt-36 pb-24" data-id="element-414" data-mp="1m5iqdyfhhlba">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-415" data-mp="1h8iv8ik6a2yy">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="max-w-2xl mb-12" data-id="element-416" data-mp="1h80naoo5e0b7">
            <h1 className="text-4xl md:text-5xl font-black text-kitty-dark mb-4 tracking-tight" data-id="element-417" data-mp="1r4dgzmjp0e5y">
              Contact KittyPot
            </h1>
            <p className="text-lg text-gray-500" data-id="element-418" data-mp="yikc97eu6pgj">
              Email us and a real person will reply. We aim to get back to you
              within two business days.
            </p>
          </motion.div>

          {/* Primary contact */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="rounded-3xl bg-gray-950 text-white p-8 md:p-12 mb-6" data-id="element-419" data-mp="18eno54zhq2go">
            <div className="w-12 h-12 rounded-2xl bg-white/[0.08] flex items-center justify-center mb-6" data-id="element-420" data-mp="2re4soel3ma7d">
              <MailIcon className="w-6 h-6 text-kitty-yellow" aria-hidden="true" data-id="element-421" data-mp="2w5mt91j7m8rt" />
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold mb-3" data-id="element-422" data-mp="2hedy6cwjfv7a">
              {SUPPORT_EMAIL}
            </h2>
            <p className="text-gray-400 max-w-lg mb-8" data-id="element-423" data-mp="3a54o9yn3mi9m">
              One address for support, questions, feedback, privacy requests and
              press enquiries. Please do not include passwords or bank details
              in your message.
            </p>
            <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center justify-center gap-2 bg-kitty-yellow hover:bg-kitty-yellow-500 text-kitty-dark px-8 py-4 min-h-[56px] rounded-full font-bold text-base transition-colors duration-150 active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-424" data-mp="313wotd8ujbdy">
              Email the KittyPot team
              <MailIcon className="w-5 h-5" aria-hidden="true" data-id="element-425" data-mp="2a03uzg1m6h6j" />
            </a>
          </motion.div>

          {/* What to include */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="grid md:grid-cols-3 gap-6 mb-12" data-id="element-426" data-mp="207odsky55wmp">
            {contactRoutes.map(route => <div key={route.title} className="bg-white rounded-2xl p-7 shadow-card border border-gray-100 flex flex-col" data-id="element-427" data-mp="2nxsh0r7f3ud6">
                <div className="w-10 h-10 rounded-xl bg-kitty-yellow/20 flex items-center justify-center mb-4" data-id="element-428" data-mp="2iww2zktcqru1">
                  <route.icon className="w-5 h-5 text-kitty-yellow-700" aria-hidden="true" data-id="element-429" data-mp="2ez6pi6jyx509" />
                </div>
                <h2 className="font-bold text-kitty-dark mb-2" data-id="element-430" data-mp="13gdqjvn0c5y8">
                  {route.title}
                </h2>
                <p className="text-sm text-gray-600 leading-relaxed mb-4" data-id="element-431" data-mp="3f7516259nf2n">
                  {route.body}
                </p>
                <a href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(route.subject)}`} className="mt-auto text-sm font-bold text-kitty-dark underline underline-offset-4 hover:text-kitty-yellow-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-kitty-yellow rounded" data-id="element-432" data-mp="3o5yyjuqf89h9">
                  Email us about this
                </a>
              </div>)}
          </motion.div>

          {/* Company information */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="bg-white rounded-2xl p-7 md:p-8 shadow-card border border-gray-100" data-id="element-433" data-mp="fovnh0og47yb">
            <div className="w-10 h-10 rounded-xl bg-kitty-yellow/20 flex items-center justify-center mb-4" data-id="element-434" data-mp="3ks62s10248ex">
              <MapPinIcon className="w-5 h-5 text-kitty-yellow-700" aria-hidden="true" data-id="element-435" data-mp="3ftw6to6id3s9" />
            </div>
            <h2 className="font-bold text-kitty-dark mb-3" data-id="element-436" data-mp="3tufakj3xxnom">
              Company information
            </h2>
            <address className="not-italic text-gray-600 text-sm leading-relaxed" data-id="element-437" data-mp="3owdve99my8if">
              {COMPANY.addressLines.map(line => <React.Fragment key={line} data-id="element-438" data-mp="2ncjrss0ljex">
                  {line}
                  <br data-id="element-439" data-mp="ge7z9nx0qyrh" />
                </React.Fragment>)}
            </address>
            <p className="text-gray-600 text-sm mt-3" data-id="element-440" data-mp="gg27q2r3syeo">
              Registered in England and Wales. Company number{' '}
              {COMPANY.registrationNumber}.
            </p>
            <p className="text-gray-500 text-sm mt-4" data-id="element-441" data-mp="qcoskrjfjpgp">
              See our{' '}
              <Link to="/privacy-policy" className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-442" data-mp="levoq55ln7jl">
                Privacy Policy
              </Link>
              ,{' '}
              <Link to="/terms" className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-443" data-mp="v1g323o001tf">
                Terms of Use
              </Link>{' '}
              and{' '}
              <Link to="/delete-account" className="font-semibold text-kitty-dark underline underline-offset-2 hover:text-kitty-yellow-700" data-id="element-444" data-mp="m6jlonu4jiuh">
                Delete account
              </Link>
              .
            </p>
          </motion.div>
        </div>
      </main>
      <Footer data-id="element-445" data-mp="39qs0qh1ofzm" />
    </div>;
}